import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  ComicSearchResult,
  SOURCE_NAMES,
  SearchSource,
  isCollectedType,
  resultDetail,
  searchComics,
} from '../comicSearch';
import CoverImage from './CoverImage';

interface ComicSearchFieldProps {
  // 'volume' for the series form: series runs only
  kinds?: 'all' | 'volume';
  placeholder: string;
  onPick: (result: ComicSearchResult) => void;
}

const KIND_LABELS: Record<ComicSearchResult['kind'], string> = {
  volume: 'Series',
  issue: 'Issue',
  book: 'Book',
};

const COLLECTED_SERIES_LABELS: Record<string, string> = {
  'Trade Paperback': 'TPB series',
  Hardcover: 'HC series',
  Omnibus: 'Omnibus series',
};

// A trade's "issues" are its volumes, and its run is a TPB series
const kindLabel = (result: ComicSearchResult) => {
  if (!isCollectedType(result.seriesType)) return KIND_LABELS[result.kind];
  if (result.kind === 'issue') return 'Volume';
  if (result.kind === 'volume') return COLLECTED_SERIES_LABELS[result.seriesType ?? ''];
  return KIND_LABELS[result.kind];
};

// Debounced live search at the top of the Add dialogs. Picking a result
// fills the form below, which can still be edited.
const ComicSearchField = ({ kinds = 'all', placeholder, onPick }: ComicSearchFieldProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ComicSearchResult[]>([]);
  const [source, setSource] = useState<SearchSource | null>(null);
  // The query the shown results belong to, so "No matches" never shows mid-typing
  const [answered, setAnswered] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const handler = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await searchComics(trimmed, kinds);
        if (cancelled) return;
        setResults(data.results);
        setSource(data.source);
        setAnswered(trimmed);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setResults([]);
          setError("Search isn't available right now. You can still fill in the details below.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [query, kinds]);

  const pick = (result: ComicSearchResult) => {
    onPick(result);
    setQuery('');
    setResults([]);
  };

  const searched = answered === query.trim() && answered.length >= 2 && !loading && !error;

  return (
    <Box>
      <TextField
        fullWidth
        autoFocus
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': placeholder },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : undefined,
          },
        }}
      />

      {error && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <List
          dense
          sx={{
            mt: 1,
            maxHeight: 320,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            py: 0,
          }}
        >
          {results.map((result) => (
            <ListItemButton
              key={`${result.kind}-${result.id}`}
              onClick={() => pick(result)}
              sx={{ gap: 1.5, py: 0.75, alignItems: 'center' }}
            >
              <Box sx={{ width: 36, flexShrink: 0, borderRadius: 0.5, overflow: 'hidden' }}>
                <CoverImage src={result.thumbUrl || undefined} title={result.title} showTitle={false} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Two lines: the end of a title is often what tells results apart */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {result.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {resultDetail(result)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {kindLabel(result)}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      )}

      {searched && results.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No matches. Try fewer words, or fill in the details below.
        </Typography>
      )}

      {results.length > 0 && source && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Results from{' '}
          {Array.from(new Set([source, ...results.map((result) => result.source)]))
            .map((entry) => SOURCE_NAMES[entry])
            .join(' and ')}
        </Typography>
      )}
    </Box>
  );
};

export default ComicSearchField;
